import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge } from "@/components/ui/data";
import { Button } from "@/components/ui/button";
import { Sheet, Muted, ConfirmDialog } from "@/components/ui/overlays";
import { Input, Label } from "@/components/ui/form";
import { api } from "@/lib/api";
import { useToast } from "@/app/Toast";
import { cn, formatDateTime } from "@/lib/utils";
import { Plus, Send, RefreshCw, KeyRound, Copy } from "lucide-react";

interface Subscription {
  id: number; locationId: number | null; name: string; url: string;
  eventTypes: string[]; active: boolean; createdAt: string;
}
interface Delivery {
  id: number; subscriptionId: number; eventId: string; eventType: string;
  attemptCount: number; responseStatus: number | null; responseExcerpt: string | null;
  status: "PENDING" | "SUCCEEDED" | "FAILED" | "DEAD";
  nextAttemptAt: string | null; createdAt: string;
}

const STATUS_COLORS: Record<Delivery["status"], string> = {
  PENDING: "bg-muted text-muted-foreground",
  SUCCEEDED: "bg-emerald-100 text-emerald-900",
  FAILED: "bg-amber-100 text-amber-900",
  DEAD: "bg-rose-100 text-rose-900",
};

const EVENT_TYPES = [
  "booking.created",
  "booking.rescheduled",
  "booking.cancelled",
  "booking.status_changed",
  "booking.completed",
  "customer.created",
  "payment.recorded",
  "customer.merged",
];

export function WebhooksPage() {
  const { toast } = useToast();
  const [subs, setSubs] = useState<Subscription[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<{ subId: number; secret: string } | null>(null);
  const [rotateTarget, setRotateTarget] = useState<Subscription | null>(null);
  const [pingTarget, setPingTarget] = useState<Subscription | null>(null);

  async function load() {
    try {
      const [s, d] = await Promise.all([
        api.get<Subscription[]>("/api/v1/webhooks"),
        api.get<Delivery[]>("/api/v1/webhooks/deliveries?limit=200"),
      ]);
      setSubs(s);
      setDeliveries(d);
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Failed to load");
    }
  }

  useEffect(() => { void load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Webhooks</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
          <Button onClick={() => setCreating(true)}><Plus className="h-3.5 w-3.5" /> New subscription</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Subscriptions</CardTitle>
          <Muted>Webhook deliveries are signed with HMAC-SHA256 over <code className="rounded bg-muted px-1 text-xs">t.body</code> in the X-Shinebright-Signature header.</Muted>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Events</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subs.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell className="max-w-xs truncate font-mono text-xs">{s.url}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {s.eventTypes.map((e) => (
                        <span key={e} className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono">{e}</span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>{s.active ? "✓" : <Badge>disabled</Badge>}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDateTime(s.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setPingTarget(s)}><Send className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setRotateTarget(s)}><KeyRound className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {subs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No webhook subscriptions. Create one to start receiving events.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deliveries</CardTitle>
          <Muted>Most recent 200. The cron tick retries PENDING/FAILED with exponential backoff (1m, 5m, 30m, 2h, 12h). After 6 attempts → DEAD.</Muted>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Created</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Response</TableHead>
                <TableHead>Next retry</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveries.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="text-xs">{formatDateTime(d.createdAt)}</TableCell>
                  <TableCell>#{d.subscriptionId}</TableCell>
                  <TableCell className="font-mono text-xs">{d.eventType}</TableCell>
                  <TableCell>
                    <span className={cn("rounded px-2 py-0.5 text-[10px] font-semibold", STATUS_COLORS[d.status])}>{d.status}</span>
                  </TableCell>
                  <TableCell>{d.attemptCount}</TableCell>
                  <TableCell className="text-xs">
                    {d.responseStatus ? `${d.responseStatus} ` : ""}
                    {d.responseExcerpt ? <span className="text-muted-foreground">{d.responseExcerpt.slice(0, 60)}</span> : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{d.nextAttemptAt ? formatDateTime(d.nextAttemptAt) : "—"}</TableCell>
                </TableRow>
              ))}
              {deliveries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">No deliveries yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CreateSheet
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(sub, secret) => {
          setCreating(false);
          setRevealed({ subId: sub.id, secret });
          void load();
        }}
      />

      {revealed && (
        <Sheet open onClose={() => setRevealed(null)} title="Subscription created" description="Copy the secret now. It is shown ONCE.">
          <div className="space-y-3">
            <div>
              <Label>Signing secret</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all rounded bg-muted px-3 py-2 font-mono text-xs">{revealed.secret}</code>
                <Button size="sm" variant="outline" onClick={() => {
                  void navigator.clipboard.writeText(revealed.secret);
                  toast("success", "Secret copied to clipboard");
                }}><Copy className="h-3.5 w-3.5" /> Copy</Button>
              </div>
              <Muted>Use this as the HMAC key to verify the X-Shinebright-Signature header on incoming requests.</Muted>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setRevealed(null)}>I've stored it</Button>
            </div>
          </div>
        </Sheet>
      )}

      <ConfirmDialog
        open={rotateTarget != null}
        onClose={() => setRotateTarget(null)}
        title="Rotate signing secret?"
        description="A new secret will be generated and shown ONCE. The current secret stops working immediately — pending deliveries from before rotation will be retried under the new secret."
        confirmLabel="Rotate"
        destructive
        onConfirm={async () => {
          if (!rotateTarget) return;
          try {
            const r = await api.post<{ secret: string }>(`/api/v1/webhooks/${rotateTarget.id}/rotate-secret`);
            setRevealed({ subId: rotateTarget.id, secret: r.secret });
            await load();
          } catch (e) {
            toast("error", e instanceof Error ? e.message : "Rotate failed");
          }
        }}
      />

      <ConfirmDialog
        open={pingTarget != null}
        onClose={() => setPingTarget(null)}
        title="Send a synthetic test event?"
        description={`A ping.test event will be POSTed to ${pingTarget?.url} using the current signing secret. Useful for verifying endpoint connectivity after setup.`}
        confirmLabel="Send ping"
        onConfirm={async () => {
          if (!pingTarget) return;
          try {
            const r = await api.post<{ ok: boolean; eventId: string }>(`/api/v1/webhooks/ping`, { subscriptionId: pingTarget.id });
            toast(r.ok ? "success" : "error", `Ping ${r.ok ? "delivered (2xx)" : "received non-2xx"} (event ${r.eventId})`);
            await load();
          } catch (e) {
            toast("error", e instanceof Error ? e.message : "Ping failed");
          }
        }}
      />
    </div>
  );
}

function CreateSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (sub: { id: number; name: string }, secret: string) => void }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [eventTypes, setEventTypes] = useState<Set<string>>(new Set(["*"]));
  const [locationId, setLocationId] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setUrl(""); setLocationId("");
      setEventTypes(new Set(["*"]));
    }
  }, [open]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (eventTypes.size === 0) { toast("error", "Pick at least one event"); return; }
    setSaving(true);
    try {
      const events = Array.from(eventTypes);
      const body: Record<string, unknown> = { name, url, eventTypes: events };
      if (locationId) body.locationId = Number(locationId);
      const r = await api.post<{ id: number; secret: string }>("/api/v1/webhooks", body);
      onCreated({ id: r.id, name }, r.secret);
    } catch (err) {
      toast("error", err instanceof Error ? err.message : "Create failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="New webhook subscription">
      <form onSubmit={save} className="space-y-3">
        <div>
          <Label>Name</Label>
          <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Make.com integration" />
        </div>
        <div>
          <Label>Endpoint URL</Label>
          <Input required type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.example.com/..." />
        </div>
        <div>
          <Label>Events</Label>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {["*", ...EVENT_TYPES].map((e) => (
              <label key={e} className="flex items-center gap-2 rounded border bg-muted/30 px-3 py-2">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={eventTypes.has(e)}
                  onChange={(ev) => {
                    const n = new Set(eventTypes);
                    if (ev.currentTarget.checked) {
                      n.add(e);
                    } else {
                      n.delete(e);
                    }
                    if (e === "*") {
                      n.clear();
                      n.add("*");
                    } else {
                      n.delete("*");
                    }
                    setEventTypes(n);
                  }}
                />
                <span className="font-mono text-xs">{e}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label>Location (optional — null = global)</Label>
          <Input type="number" value={locationId} onChange={(e) => setLocationId(e.target.value)} placeholder="leave blank to receive all locations" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Creating…" : "Create subscription"}</Button>
        </div>
      </form>
    </Sheet>
  );
}
